<script>
    import {onMount, setContext, createEventDispatcher, onDestroy} from "svelte";
    import {writable} from "svelte/store";

    import {geoTransform, geoPath} from "d3-geo";
    import L from "leaflet";

    let projection = writable(undefined);

    /**
     * the height of the map. Required so Leaflet knows how to scale the map
    */
   export let height;

   /**
    * The width of the map. Defaults to 100%
    */
   export let width = undefined;

   $: widthText = width === undefined ? "100%" : `${width}px`

   /**
    * The id (as in, the id attribute) of the map
   */
    export let id = "mapid";

    /**
     * The description of the map. Used to create an aria-label for the container
    */
   export let description;

   /**
    * Any options from https://leafletjs.com/reference-1.7.1.html#map-factory
   */
    export let options = {};

    /**
     * The center of the map.
     */
    export let center = [37.96747811844137, -79.30716991424562];

    /**
     * The zoom level of the map
    */
    export let zoom = 7;

    const dispatch = createEventDispatcher();

    function mapDispatch(eventType) {
        return function wrapper(e) {
            dispatch(eventType, Object.assign({getMap: () => map}, e));
        }
    }

    $: mapOpts = Object.assign({
        center: center,
        zoom: zoom
    }, options);

    let map;

    $: if (map) {
        // map.setView(center);
        map.setView(center, zoom);
    }

    function setProjection() {
        // https://bost.ocks.org/mike/leaflet/
        function projectPoint(x, y) {
            var point = map.latLngToLayerPoint(new L.LatLng(y, x));
            this.stream.point(point.x, point.y);
        }

        var transform = geoTransform({point: projectPoint});
        projection.update(_n => geoPath().projection(transform));
    }

    function addHomeButton() {
        /**
         * adds a home button centering the user at the original position
         * */
        const homeButton = document.createElement("a");
        homeButton.className += "fas fa-home g-home";
        homeButton.setAttribute("aria-label", "Re-center map")
        homeButton.addEventListener("click", function() {
            map.setView(center, zoom);
        });
        const zoomBar = document.getElementById(id)
            .getElementsByClassName("leaflet-bar")
            .item(0);
        zoomBar.appendChild(homeButton);
    }

    onMount(() => {
        map = L.map(id, mapOpts);
        addHomeButton();
        setProjection();
        map.on("zoom", function(e) {
            setProjection();
            mapDispatch("zoom")(e);
        });
        map.on("resize", function(e) {
            map.invalidateSize();
            setProjection();
            mapDispatch("resize")(e);
        });

        [
            "zoomlevelschange", 
            "unload", 
            "load", 
            "zoomstart", 
            "movestart", 
            "move", 
            "zoomend", 
            "moveend", 
            "locationerror", 
            "locationfound",
            "click",
            "dblclick",
            "mousedown",
            "mouseup",
            "mouseover",
            "mouseout",
            "contextmenu",
            "keypress",
            "keydown",
            "keyup"
        ].forEach(d => map.on(d, mapDispatch(d)));
    })

    onDestroy(() => {
        map.remove();
    })

    setContext("mapContainer", {
        getMap: () => map,
        projection: projection
    });
</script>

<div class="vpap-leaflet" id={id} style={`height: ${height}px; width: ${widthText}`} aria-label={description}>
    {#if map}
    <slot></slot>
    {/if}
</div>

<style>
    div.vpap-leaflet {
        position: relative;
        pointer-events: auto;
    }

    div.vpap-leaflet > :global(.leaflet-control-container > div > .leaflet-bar > .fas.fa-home.g-home) {
        cursor: pointer;
    }
</style>